import { cn } from '../utils/utils';

export interface SegmentedTabItem<T extends string = string> {
    key: T;
    label: string;
}

interface SegmentedTabsProps<T extends string = string> {
    items: SegmentedTabItem<T>[];
    value: T;
    onChange: (key: T) => void;
    className?: string;
}

/**
 * Shared pill-style tab switcher — the design-system tab bar (rounded-full
 * container, active pill in tenant primary). Replaces MUI `<Tabs>`, whose
 * default uppercase styling is off-brand. Mirrors the dashboard date-range
 * toggle so every surface shares one tab look; labels render as authored
 * (sentence case), never uppercased.
 */
function SegmentedTabs<T extends string = string>({
    items,
    value,
    onChange,
    className,
}: SegmentedTabsProps<T>) {
    return (
        <div
            className={cn(
                'flex gap-2 bg-muted rounded-full p-1 max-w-full w-fit',
                'overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent',
                'whitespace-nowrap scroll-smooth',
                className,
            )}
        >
            {items.map((item) => {
                const isActive = item.key === value;
                return (
                    <button
                        key={item.key}
                        type={'button'}
                        data-active={isActive ? 'true' : 'false'}
                        onClick={() => onChange(item.key)}
                        className={cn(
                            'px-4 sm:px-6 py-2 rounded-full font-medium text-sm',
                            'transition-colors duration-150 flex-shrink-0',
                            'focus:outline-none focus:ring-2 focus:ring-offset-2',
                            isActive
                                ? 'bg-primary text-text-buttonContrast shadow-sm'
                                : 'text-gray-500 hover:text-gray-700',
                        )}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}

export default SegmentedTabs;
